'use client';

import { Image } from '@packages/shared/src/ui/Image/Image';
import { Link } from '@packages/modules/src/Link/Link';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { Card } from '@packages/shared/src/ui/Card/Card';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@packages/shared/src/ui/HoverCard/HoverCard';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { Flag } from '@packages/shared/src/ui/Flag/Flag';
import { type VariantProps } from 'class-variance-authority';
import type { flagVariants } from '@packages/shared/src/ui/Flag/Flag.variants';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { type FragmentType, getFragmentData } from '@packages/gql/generated/shopping';
import type {
  LineItemFragmentFragment,
  CheckoutAdditionalFlagType,
} from '@packages/gql/generated/shopping/graphql';
import { LineItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/LineItemFragmentFragmentDoc';
import type { GlycerinCartProduct } from '@packages/tracking/src/types/types';
import { MiniDVLayer } from '@packages/modules/src/MiniDVLayer/MiniDVLayer.client';
import { PowerSupplyLocalized } from '@packages/modules/src/PowerSupplyLocalized/PowerSupplyLocalized.client';
import { AvailabilityInfo } from '@packages/modules/src/AvailabilityInfo/AvailabilityInfo.client';
import { buildGlycerinCartProduct } from '@packages/modules/src/helpers/buildGlycerinObject/buildGlycerinObject';
import { useState } from 'react';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { GTMEventGlycerinProductClick } from '@packages/tracking/src/types/events';
import { useMediaType } from '@packages/shared/src/hooks/useMediaType/useMediaType';
import { PowerEfficiency } from '@packages/modules/src/PowerEfficiency/PowerEfficiency';
import { MoreMenu } from '../MoreMenu/MoreMenu';
import { ActionBar } from '../ActionBar/ActionBar';
import { Aplus } from '../Aplus/Aplus';
import { EditAction } from '../Actions/EditAction';
import { AddToWishlistAction } from '../Actions/AddToWishlistAction';
import { DeleteAction } from '../Actions/DeleteAction';
import { LineItemPrice } from './LineItemPrice';

const messages = defineMessages({
  lineItemConfigurationSERIALNUMBER: {
    id: 'LineItem.Configuration.SerialNumber',
    defaultMessage: 'Bedienelement / Seriennummer: {serialNumber}',
  },
  lineItemConfigurationWIDTH: {
    id: 'LineItem.Configuration.Width',
    defaultMessage: 'Breite: {width} cm',
  },
  lineItemConfigurationLENGTH: {
    id: 'LineItem.Configuration.Length',
    defaultMessage: 'Länge: {length} cm',
  },
  lineItemConfigurationENGRAVING: {
    id: 'LineItem.Configuration.Engraving',
    defaultMessage: 'Ihr Gravurtext: {engraving}',
  },
  sustainedFlag: {
    id: 'LineItem.SustainedFlag',
    defaultMessage: 'Nachhaltig',
  },
});

const getFlagVariant = (
  flagType: CheckoutAdditionalFlagType | undefined | null,
): VariantProps<typeof flagVariants>['variant'] => {
  const typeVariantMap: Record<
    CheckoutAdditionalFlagType,
    NonNullable<VariantProps<typeof flagVariants>['variant']>
  > = {
    CUSTOM: 'contrast',
    NEUTRAL: 'neutral',
    SALE: 'sale',
    SECONDARY: 'secondary',
    SUSTAINABILITY: 'sustainability',
  };

  return flagType ? typeVariantMap[flagType] : undefined;
};

type LineItemActionsProps = {
  sku: LineItemFragmentFragment['product']['sku'];
  lineItemId: LineItemFragmentFragment['id'];
  glycerinProduct: GlycerinCartProduct;
  quantity: LineItemFragmentFragment['quantity'];
  akl: LineItemFragmentFragment['product']['akl'];
};

const LineItemActions: React.FC<LineItemActionsProps> = ({
  lineItemId,
  sku,
  glycerinProduct,
  quantity,
  akl,
}) => {
  const isDesktop = useMediaType();
  const [isMiniDVOpen, setIsMiniDVOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const children = [
    <EditAction
      mode="basket"
      key="edit"
      onClick={() => {
        setIsMiniDVOpen(true);
        setIsMoreMenuOpen(false);
      }}
    />,

    <AddToWishlistAction
      key="wishlist"
      sku={sku}
      lineItemId={lineItemId}
      glycerinProduct={glycerinProduct}
      onClick={() => {
        setIsMoreMenuOpen(false);
      }}
    />,
    <DeleteAction
      mode="basket"
      key="delete"
      lineItemId={lineItemId}
      glycerinProduct={glycerinProduct}
      onClick={() => {
        setIsMoreMenuOpen(false);
      }}
    />,
  ];

  return (
    <>
      {isDesktop ? (
        <ActionBar>{children}</ActionBar>
      ) : (
        <MoreMenu isOpen={isMoreMenuOpen} onOpenChange={setIsMoreMenuOpen}>
          {children}
        </MoreMenu>
      )}
      {lineItemId && (
        <MiniDVLayer
          sku={sku}
          akl={akl}
          lineItemId={lineItemId}
          quantity={quantity}
          open={isMiniDVOpen}
          onClose={() => setIsMiniDVOpen(false)}
          mode="ATB"
        />
      )}
    </>
  );
};

/* GraphQL */ `
  fragment LineItemFragment on BasketLineItem {
    id
    product {
      akl
      sku
      orderNumber
      promotion
      imageUrl
      name
      brand
      powerEfficiencyFlags {
        text
        iconText
        level
        link
        icon
        dataSheet
      }
      variation
      sustained
      marketingFlag {
        text
        type
      }
      powerSupply {
        chargerType
        maxPower
        minPower
        powerSupplyIncluded
      }
      additionalFlags {
        src
        text
        type
      }
    }
    price {
      amount
      currency
      isStartPrice
      saving {
        type
        value
      }
      unit
      uvp
      value
      valueOld
      valueUnit
      discount {
        discountAmount
        discountPercentage
        amountBeforeDiscount
      }
    }
    singlePrice {
      amount
      currency
    }
    availability {
      message
      state
      limitedStock
    }
    configuration {
      length
      width
      engraving
      serialNumber
    }
    aplus {
      ...AplusFragment
    }
    quantity
    isSoldOut
  }
`;

type LineItemProps = {
  lineItemData: FragmentType<typeof LineItemFragmentFragmentDoc>;
  lastItem: boolean;
};

export const LineItem = ({ lineItemData, lastItem = false }: LineItemProps) => {
  const fragmentData = getFragmentData(LineItemFragmentFragmentDoc, lineItemData);
  const { formatPrice } = useFormatPrice();
  const {
    availability,
    configuration,
    id,
    isSoldOut,
    price,
    product,
    quantity,
    singlePrice,
    aplus,
  } = fragmentData;
  const glycerinProduct = buildGlycerinCartProduct(fragmentData);

  const { language, localizeUrl } = useI18n();
  const { formatMessage } = useIntl();
  const productLinkParams = new URLSearchParams({ sku: product.sku }).toString();
  const dispatchGTMEvent = useTracking();

  const handleProductClick = () => {
    dispatchGTMEvent<GTMEventGlycerinProductClick>({
      event: 'ProductClick',
      ProductClickData: {
        category: 'Cart',
        productId: product.akl,
        articleId: glycerinProduct.articleId,
        skuId: product.sku,
      },
    });
  };

  return (
    <Card className="relative mb-1.5 w-full overflow-visible pt-1.5 last:mb-0 md:mb-2 last:md:mb-0">
      <div className="flex flex-nowrap gap-1.5 overflow-visible px-1.5 pb-1.5">
        {/* Image Section */}
        <HoverCard>
          <HoverCardTrigger asChild>
            <Link
              className="relative flex size-[80px] flex-shrink-0 flex-col items-center justify-center md:size-[120px]"
              href={localizeUrl(`/p/${product.akl}?${productLinkParams}`, language)}
              onClick={handleProductClick}
            >
              <Image
                className="object-contain"
                priority
                unoptimized
                alt={product.name}
                src={product.imageUrl}
                fill
              />
              {product.sustained && (
                <Flag
                  text={formatMessage(messages.sustainedFlag)}
                  variant="sustainability"
                  className="absolute top-2.5 left-0 overflow-hidden"
                />
              )}
            </Link>
          </HoverCardTrigger>
          <HoverCardContent side="right" className="bg-color-background-base hidden p-2 md:block">
            <div className="relative h-[240px] w-[240px]">
              <Image
                className="object-contain"
                priority
                unoptimized
                alt={product.name}
                src={product.imageUrl}
                fill
              />
            </div>
          </HoverCardContent>
        </HoverCard>
        {/* Product Details Section */}
        <div className="flex flex-grow flex-col gap-1.5">
          <div className="flex flex-col gap-2 md:flex-row md:flex-nowrap">
            <div className="flex-grow">
              {product.marketingFlag && (
                <Flag text={product.marketingFlag.text} variant="contrast" />
              )}
              {product.additionalFlags && product.additionalFlags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {product.additionalFlags.map((flag) =>
                    flag.src ? (
                      <div
                        className="relative h-[1.5rem] max-h-[1.5rem] w-[5.625rem] max-w-[5.625rem]"
                        key={flag.text}
                      >
                        <Image src={`${flag.src}.png`} alt={flag.text} fill />
                      </div>
                    ) : (
                      <Flag key={flag.text} text={flag.text} variant={getFlagVariant(flag.type)} />
                    ),
                  )}
                </div>
              )}
              <div className="mr-5 pb-2 md:mr-1">
                <h4 className="mb-0 leading-tight font-semibold md:mb-1">{product.name}</h4>
                <p className="text-size-body-sm">{product.variation}</p>
                {configuration &&
                  Object.entries(configuration).map(
                    ([key, value]) =>
                      key !== '__typename' &&
                      value && (
                        <p className="text-color-text-secondary text-size-body-sm" key={key}>
                          {formatMessage(
                            messages[
                              `lineItemConfiguration${key.toUpperCase()}` as keyof typeof messages
                            ],
                            { [key]: value },
                          )}
                        </p>
                      ),
                  )}
              </div>
              <p className="text-size-body-sm inline-block pb-1">
                <FormattedMessage
                  key="LineItem.Quantity-text"
                  id="LineItem.Quantity"
                  defaultMessage="Anzahl: {quantity}"
                  values={{
                    quantity,
                  }}
                />
              </p>
              {singlePrice && singlePrice.amount && (
                <div className="text-size-body-sm ml-1 inline-block">
                  <FormattedMessage
                    key="LineItem.SinglePrice-text"
                    id="LineItem.SinglePrice"
                    defaultMessage=" (à {singlePrice})"
                    values={{
                      singlePrice: formatPrice(
                        singlePrice.amount,
                        singlePrice.currency satisfies string,
                      ),
                    }}
                  />
                </div>
              )}
              {availability.limitedStock && (
                <div>
                  <Flag
                    text={formatMessage({
                      id: 'LineItem.LimitedStock',
                      defaultMessage: 'Fast ausverkauft',
                    })}
                    variant="contrast"
                    className="bg-color-text-dark"
                  />
                </div>
              )}
              <AvailabilityInfo
                availabilityMessage={availability.message}
                availabilityState={availability.state}
              />
              <div className="flex flex-col pt-1">
                <PowerEfficiency efficiency={product.powerEfficiencyFlags} />
              </div>
              {product.powerSupply && (
                <PowerSupplyLocalized
                  minPower={product.powerSupply.minPower}
                  maxPower={product.powerSupply.maxPower}
                  chargerType={product.powerSupply.chargerType}
                  powerSupplyIncluded={product.powerSupply.powerSupplyIncluded}
                  showPowerSupplyMessage
                />
              )}
            </div>
            {!isSoldOut && <LineItemPrice price={price} />}
          </div>
        </div>
      </div>
      {/* APlus Section */}
      <div className="mt-0.5">
        {!!aplus && (
          <>
            <Divider />
            <Aplus aplusData={aplus} lineItemId={id!} quantity={quantity} />
          </>
        )}
      </div>
      {/* Actions Section */}
      <LineItemActions
        sku={product.sku}
        lineItemId={id}
        glycerinProduct={glycerinProduct}
        akl={product.akl}
        quantity={quantity}
      />
    </Card>
  );
};
